import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    Injector,
    inject,
    input,
    output,
    untracked,
} from '@angular/core';
import { areButtonStatesEqual, BUTTON_INITIAL_STATE } from './cost-to-build-client-ui-button.state';
import { ButtonStore } from './cost-to-build-client-ui-button.store';
import type { ButtonSize, ButtonState, ButtonType, ButtonVariant } from './cost-to-build-client-ui-button.types';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    providers: [ButtonStore],
    selector: 'ctb-button',
    styleUrl: './cost-to-build-client-ui-button.scss',
    templateUrl: './cost-to-build-client-ui-button.html',
})
export class CtbButtonComponent {
    readonly #injector = inject(Injector);
    readonly #store = inject(ButtonStore);

    readonly ariaLabel = input<string>(BUTTON_INITIAL_STATE.ariaLabel);
    readonly disabled = input(BUTTON_INITIAL_STATE.disabled, { transform: booleanAttribute });
    readonly id = input<string>(BUTTON_INITIAL_STATE.id);
    readonly label = input<string>(BUTTON_INITIAL_STATE.label);
    readonly loading = input(BUTTON_INITIAL_STATE.loading, { transform: booleanAttribute });
    readonly size = input<ButtonSize>(BUTTON_INITIAL_STATE.size);
    readonly type = input<ButtonType>(BUTTON_INITIAL_STATE.type);
    readonly variant = input<ButtonVariant>(BUTTON_INITIAL_STATE.variant);

    readonly clicked = output<MouseEvent>();
    readonly keyDown = output<KeyboardEvent>();
    readonly keyPress = output<KeyboardEvent>();
    readonly keyUp = output<KeyboardEvent>();
    readonly stateChange = output<ButtonState>();

    readonly isDisabled = this.#store.isDisabled;
    readonly resolvedAriaLabel = computed(() => this.state().ariaLabel ?? this.state().label);
    readonly state = this.#store.state;

    #lastEmittedState: ButtonState | null = null;

    readonly syncStoreFromInputs = effect(() => this.#syncStoreFromInputs(), { injector: this.#injector });

    readonly emitStateChanges = effect(
        () => {
            const nextState = this.state();

            const previousState = this.#lastEmittedState;
            if (previousState !== null && areButtonStatesEqual(previousState, nextState)) {
                return;
            }

            this.#lastEmittedState = nextState;
            this.stateChange.emit(nextState);
        },
        { injector: this.#injector }
    );

    onButtonClick(event: MouseEvent) {
        event.preventDefault();

        if (this.isDisabled()) {
            return;
        }

        this.#store.registerClick();
        this.clicked.emit(event);
    }

    onButtonKeyDown(event: KeyboardEvent) {
        if (this.isDisabled()) {
            return;
        }

        this.keyDown.emit(event);
    }

    onButtonKeyPress(event: KeyboardEvent) {
        if (this.isDisabled()) {
            return;
        }

        this.keyPress.emit(event);
    }

    onButtonKeyUp(event: KeyboardEvent) {
        if (this.isDisabled()) {
            return;
        }

        this.keyUp.emit(event);
    }

    #syncStoreFromInputs() {
        const patch = {
            ariaLabel: this.ariaLabel(),
            disabled: this.disabled(),
            id: this.id(),
            label: this.label(),
            loading: this.loading(),
            size: this.size(),
            type: this.type(),
            variant: this.variant(),
        };

        untracked(() => this.#store.setState(patch));
    }
}
